import React, { useMemo, useReducer, useState, useRef } from 'react';
import cloneDeep from 'lodash.clonedeep';
import loadable from '@loadable/component';
import { Helmet } from 'react-helmet-async';

// apis
import researchApi from '/src/apis/researchApi';
import taxonomyApi from '/src/apis/taxonomyApi';

// constants
import { WEB_URL } from '/src/utilities/endpoints';

// helpers
import { prepareFilterParam } from '/src/utilities/queryParams';

// reusable components
import MagnittBanner from '/src/micro-frontends/MagnittBanner';
import NewsletterWidget from '/src/micro-frontends/NewsletterWidget';
import SideBanner from '/src/micro-frontends/SideBannerWidget';
const ResearchRow = loadable(() => import(/* webpackChunkName: "ResearchRow" */ './ResearchRow'));
const FeaturedContent = loadable(() =>
    import(/* webpackChunkName: "Featured" */ '/src/reusable-components/FeaturedContent'),
);

// analytics
import AmplitudeHelperClass from '/src/utilities/amplitudeHelper';
const amplitudeHelper = new AmplitudeHelperClass();

// research filter helpers
import { initialFilters, filterReducer } from './helpers/filterState';
import Skeleton from 'react-loading-skeleton';
const Filters = loadable(() => import(/* webpackChunkName: "Filters" */ './Filters'));

const ResearchPage = (props) => {
    // filters
    const filterRef = useRef();
    const [filterFields, setFilterFields] = useReducer(filterReducer, cloneDeep(initialFilters));

    // Internal states
    const [searchData, setSearchData] = useState(null);
    const [ShowMoreLoading, setShowMoreLoading] = useState(false);

    // Count Data
    const [countData, setCountData] = useReducer(
        (prevState, updatedProperty) => ({ ...prevState, ...updatedProperty }),
        {
            page: 1,
            count: 0,
            limit: 6,
            specificCounts: { total: 0, ar_count: 0, en_count: 0, free_count: 0, paid_count: 0 },
        },
    );

    // UseMemo will memoize the data and not execute the method on every render
    const featuredResearchData = useMemo(() => {
        if (__isBrowser__) {
            return window.__INITIAL_DATA__.data;
        } else {
            return props.staticContext.data;
        }
    }, []);

    // get taxo list // catg, industry, country
    const taxonomyList = useMemo(() => {
        let taxoList = {};
        researchApi.getResearchCountriesCount().then((response) => {
            if (response?.length > 0) {
                taxoList.countries = [];
                // label, value parameter to match format expected by react-select
                response.map((loc) => {
                    const label = loc.name + ' (' + loc.research_count + ')';
                    taxoList.countries.push({ label, value: loc.id });
                });
            }
        });
        researchApi.getResearchIndustriesCount().then((response) => {
            if (response?.length > 0) {
                taxoList.industries = [];
                // label, value parameter to match format expected by react-select
                response.map((ind) => {
                    const label = ind.name + ' (' + ind.research_count + ')';
                    taxoList.industries.push({ label, value: ind.id });
                });
            }
        });

        taxonomyApi.getResearchCategories().then((response) => {
            if (response?.length > 0) {
                taxoList.categories = [];
                // label, value parameter to match format expected by react-select
                response.map((item) => {
                    taxoList.categories.push({ label: item.name, value: item.id });
                });
            }
        });
        return taxoList;
    }, []);

    const getSearchData = async ({ currentFilters, page }) => {
        const researchData = await researchApi.getResearchListings(
            countData.limit,
            currentFilters,
            page,
        );
        const researchSettingData = await researchApi.getResearchSettingCount(currentFilters);
        return { researchData, researchSettingData };
    };

    const filterValues = async ({ filterFields, page }) => {
        page = !page ? 1 : page; // resets page when new filter is called
        const currentFilters = prepareFilterParam(filterFields);
        const { researchData, researchSettingData } = await getSearchData({ currentFilters, page });

        if (page > 1) {
            setSearchData((prev) => prev.concat(researchData));
        } else {
            setSearchData(researchData);
        }

        setShowMoreLoading(false);

        // Set Search Count
        if (researchSettingData.length > 0) {
            setCountData({
                page,
                specificCounts: researchSettingData[0],
                count: researchSettingData[0].total,
            });
        }

        const isFilteredUrl = location.search.length > 0;
        if (isFilteredUrl && document?.getElementById('articles-section')) {
            document.getElementById('articles-section').scrollIntoView();
        }
    };

    const handleShowMore = () => {
        setShowMoreLoading(true);
        let { limit, page, count } = countData;
        page = parseInt(page, 10);

        // Only increment the page if there are more records exists
        if (count > limit * page) {
            page = page + 1;
            setCountData({ page: page });
            filterValues({ filterFields, page });
        }
    };

    // render functions

    const renderContent = useMemo(() => {
        const hasFilter = filterFields.some((x) => x.value.length > 0);

        if (searchData && searchData.length > 0) {
            return (
                <div className="row responsive-row">
                    {searchData.map((item, i) => (
                        <ResearchRow key={i} data={item} />
                    ))}
                </div>
            );
        } else {
            return hasFilter ? null : (<div className="row responsive-row">{[...Array(6)].map((_, index) => (
                <div className="col-md-12 col-lg-6 responsive-margin-bottom">
                    <Skeleton height={224} width={'100%'} />
                </div>
            ))} </div>)
        }
    }, [searchData]);

    const renderShowMore = useMemo(() => {
        if (searchData && searchData.length < countData.count) {
            return (
                <div className="full-width-show-more mb-0">
                    <span
                        className="red"
                        style={{ fontSize: 14.5 }}
                        onClick={(event) => {
                            handleShowMore();
                        }}
                    >
                        {ShowMoreLoading ? (
                            <i className="fas fa-circle-notch fa-spin mr-2"></i>
                        ) : (
                            <i className="fas fa-arrow-circle-down mr-2"></i>
                        )}
                        SHOW MORE
                    </span>
                </div>
            );
        }
        return null;
    }, [countData, searchData]);

    const renderNoResults = useMemo(() => {
        if (searchData && searchData.length === 0) {
            return (
                <div className="content-sub-header">
                    No results were found. Please change your keywords or{' '}
                    <a className="red" onClick={() => filterRef.current.clearFilter()}>
                        return to the main listing
                    </a>
                    .
                </div>
            );
        }
        return null;
    }, [searchData]);

    const searchHeader = useMemo(() => {
        // display number of results (based on page count: countData.limit)
        // if displayed results are less than page count, display the total number of results instead
        const total = countData.specificCounts.total;
        const count = countData.limit * countData.page;
        const resultsShown = count > total ? total : count;
        const resultString = `Showing ${resultsShown} out of ${total} results`;

        // get title for filtered url // keyword // country // industry // catg
        let arr = [];
        filterFields.map((x) => {
            if (x.value.length > 0) {
                let taxonomyToCheck;
                if (x.key == 'keyword') {
                    arr.push(`"${x.value[0]}"`);
                } else if (x.key == 'location_id') {
                    taxonomyToCheck = taxonomyList.countries;
                } else if (x.key == 'magnitt_industry') {
                    taxonomyToCheck = taxonomyList.industries;
                } else if (x.key == 'category') {
                    taxonomyToCheck = taxonomyList.categories;
                }

                if (taxonomyToCheck) {
                    taxonomyToCheck.map((y) => {
                        let name = y.name ? y.name : y.label;
                        let id = y.id ? y.id : y.value;
                        if (id == x.value[0]) {
                            arr.push(name);
                            amplitudeHelper.trackEvent(
                                'research dir - searched by ' + x.name + ' tag',
                            );
                        }
                    });
                }
            }
        });

        let tagsList = arr[0];
        if (arr.length > 1) {
            // for multiples, add ',' between each term
            tagsList = arr.slice(0, arr.length - 1).join(', ') + ' and ' + arr.slice(-1);
        }

        return (
            <div className="db-box responsive-margin-bottom">
                <h2 className="content-header">
                    {arr.length === 0 ? 'Latest Research' : 'Search Results'}
                </h2>
                {searchData && searchData.length > 0 && (
                    <div className="content-sub-header">
                        {arr.length === 0 ? (
                            <>{resultString}</>
                        ) : (
                            <>
                                {resultString} for{' '}
                                <span style={{ fontWeight: 'bold' }}>{tagsList}</span>
                            </>
                        )}
                    </div>
                )}
                {renderNoResults}
            </div>
        );
    }, [countData, filterFields, searchData]);

    const helpdeskSegTrack = () => {
        amplitudeHelper.trackEvent('Helpdesk Article Clicked', {
            clicked_from: 'Research Directory',
            article_viewed: "https://helpdesk.magnitt.com/venture-investment-reports-available-on-the-platform"
        });
    }
    const featuredResearchImage  = featuredResearchData?.length > 0 ?  featuredResearchData[0]?.image : '';

    return (
        <div className="footer-margin-offset">
            <Helmet>
                <title>Emerging Venture Markets Reports | MAGNiTT</title>
                <link rel="canonical" href={`${WEB_URL}/research`} />
                <link rel="preload" href={featuredResearchImage} as='image' fetchPriority='high'/>
                <meta
                    name="description"
                    content="Extensive research on the Middle East, Africa, Pakistan and Turkey Startup ecosystem. Research covers analytics on funding, investors, industry and geography including the UAE, Saudi Arabia, Pakistan, and Egypt."
                />
                <meta
                    name="abstract"
                    content="Extensive research on the Middle East, Africa, Pakistan and Turkey Startup ecosystem. Research covers analytics on funding, investors, industry and geography including the UAE, Saudi Arabia, Pakistan, and Egypt."
                />
                <meta property="og:type" content="article" />
                <meta property="og:url" content={`${WEB_URL}/research`} />
                <meta property="og:title" content="Emerging Venture Markets Reports | MAGNiTT" />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "BreadcrumbList",
                        "itemListElement": [{
                            "@type": "ListItem",
                            "position": 1,
                            "name": "Emerging Venture Markets Reports",
                            "item": `${WEB_URL}/research`
                        }]
                    })}
                </script>
            </Helmet>

            <section className="directory content-box content-page">
                {/* top widgets row */}
                <div className="row content-widgets-row">
                    <div className="widget-banner">
                        <MagnittBanner data={{ match: props.match }} />
                    </div>
                    <div className="widget-newsletter responsive-margin-bottom">
                        <NewsletterWidget
                            history={props.history}
                            data={{ pageType: 'contentDir' }}
                        />
                    </div>
                </div>

                {/* featured */}
                <div className="db-box mt-0 responsive-margin-bottom" style={{ borderRadius: 5 }}>
                    <h2 className="content-header">Featured Research</h2>
                    <FeaturedContent data={featuredResearchData} type="research" />
                </div>

                {/* search bar, filters */}
                <div className="row content-search-row">
                    <div className="content-search-box">
                        <div className="db-box mt-0" style={{ borderRadius: 5, height: '100%' }}>
                            <div
                                className="page-header hero-img"
                                style={{ textAlign: 'left', maxWidth: 'none' }}
                            >
                                <div className="row">
                                    <div className="search-header">
                                        <h2 className="content-header float-left">Search</h2>
                                        {/* MAG-145: temporarily removed while helpdesk knowledge section is redesigned */}
                                        {/* <h2 className="content-header float-right">
                                            <a onClick={() => helpdeskSegTrack()} className="helptext" aria-label='help-text'  href="https://helpdesk.magnitt.com/venture-investment-reports-available-on-the-platform" target="_blank" rel="noopener noreferrer">
                                                <i className="far fa-question-circle" data-toggle="tooltip" data-placement="top" title="Help"></i>
                                            </a>
                                        </h2> */}
                                    </div>

                                    <div className="content-search-filters col-12">

                                        <Filters
                                            ref={filterRef}
                                            location={props.location}
                                            countData={countData}
                                            filterFields={filterFields}
                                            filterValues={filterValues}
                                            setCountData={setCountData}
                                            setFilterFields={setFilterFields}
                                            taxonomyList={taxonomyList}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="content-side-banner">
                        <SideBanner data={{ match: props.match }} />
                    </div>
                </div>

                {/* latest research / search results */}
                <div id="articles-section" className="content-block responsive-margin-top">
                    <div className="right-side">
                        <div
                            className="newsContent see-more-list"
                            style={{ borderRadius: 5, background: 'unset', boxShadow: 'unset' }}
                        >
                            {searchHeader}
                            {renderContent}
                            {renderShowMore}
                        </div>
                    </div>

                    <div className="hidden-md-up">
                        <SideBanner data={{ match: props.match }} />
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ResearchPage;
